export interface WebCitation {
  type: "url_citation";
  startIndex: number;
  endIndex: number;
  url: string;
  title: string;
}

export interface WebSource {
  type: "url";
  url: string;
  title?: string;
}

export interface WebSearchMetadata {
  citations: WebCitation[];
  sources: WebSource[];
}

export interface ExtractedWebSearchOutput {
  text: string;
  metadata: WebSearchMetadata;
}

const MAX_WEB_URL_LENGTH = 8_192;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sourceTitle(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const title = value.trim();
  return title || undefined;
}

function fallbackTitle(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/** Returns a canonical HTTP(S) URL, or null for unsafe/malformed values. */
export function normalizeWebUrl(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0 || value.length > MAX_WEB_URL_LENGTH || value !== value.trim()) return null;
  if (/[\u0000-\u001f\u007f]/.test(value)) return null;

  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.href;
  } catch {
    return null;
  }
}

/** Canonicalizes and deduplicates source-like values while preserving first-seen order. */
export function dedupeWebSources(values: readonly unknown[]): WebSource[] {
  const sources = new Map<string, WebSource>();

  for (const value of values) {
    if (!isRecord(value)) continue;
    const url = normalizeWebUrl(value.url);
    if (!url) continue;

    const title = sourceTitle(value.title);
    const existing = sources.get(url);
    if (!existing) {
      sources.set(url, { type: "url", url, ...(title ? { title } : {}) });
    } else if (!existing.title && title) {
      existing.title = title;
    }
  }

  return [...sources.values()];
}

function parseCitation(value: unknown, offset: number, textLength: number): WebCitation | null {
  if (!isRecord(value) || value.type !== "url_citation") return null;

  const startIndex = value.start_index;
  const endIndex = value.end_index;
  const url = normalizeWebUrl(value.url);
  if (
    !Number.isSafeInteger(startIndex) ||
    !Number.isSafeInteger(endIndex) ||
    typeof startIndex !== "number" ||
    typeof endIndex !== "number" ||
    startIndex < 0 ||
    endIndex <= startIndex ||
    endIndex > textLength ||
    !url
  ) return null;

  return {
    type: "url_citation",
    startIndex: offset + startIndex,
    endIndex: offset + endIndex,
    url,
    title: sourceTitle(value.title) ?? fallbackTitle(url),
  };
}

function dedupeCitations(citations: readonly WebCitation[]): WebCitation[] {
  const unique = new Map<string, WebCitation>();
  for (const citation of citations) {
    const key = `${citation.startIndex}:${citation.endIndex}:${citation.url}`;
    if (!unique.has(key)) unique.set(key, citation);
  }
  return [...unique.values()];
}

/** Extracts assistant text, URL annotations and consulted sources from a Responses API output array. */
export function extractWebSearchOutput(output: unknown): ExtractedWebSearchOutput {
  if (!Array.isArray(output)) return { text: "", metadata: { citations: [], sources: [] } };

  let text = "";
  const citations: WebCitation[] = [];
  const rawSources: unknown[] = [];

  for (const item of output) {
    if (!isRecord(item)) continue;

    if (item.type === "web_search_call" && isRecord(item.action) && Array.isArray(item.action.sources)) {
      rawSources.push(...item.action.sources);
    }

    if (item.type !== "message" || !Array.isArray(item.content)) continue;
    for (const part of item.content) {
      if (!isRecord(part) || part.type !== "output_text" || typeof part.text !== "string") continue;

      const separator = text ? "\n" : "";
      const offset = text.length + separator.length;
      text += separator + part.text;

      if (!Array.isArray(part.annotations)) continue;
      for (const annotation of part.annotations) {
        const citation = parseCitation(annotation, offset, part.text.length);
        if (citation) citations.push(citation);
      }
    }
  }

  const uniqueCitations = dedupeCitations(citations);
  return {
    text,
    metadata: {
      citations: uniqueCitations,
      sources: dedupeWebSources([...rawSources, ...uniqueCitations]),
    },
  };
}

interface MarkdownCitation extends WebCitation {
  sourceNumber: number;
}

interface CitationCluster {
  startIndex: number;
  endIndex: number;
  citations: MarkdownCitation[];
}

function validMarkdownCitations(text: string, citations: readonly WebCitation[]): MarkdownCitation[] {
  const normalized: WebCitation[] = [];
  const seenRanges = new Set<string>();

  for (const citation of citations) {
    const url = normalizeWebUrl(citation.url);
    if (
      !url ||
      !Number.isSafeInteger(citation.startIndex) ||
      !Number.isSafeInteger(citation.endIndex) ||
      citation.startIndex < 0 ||
      citation.endIndex <= citation.startIndex ||
      citation.endIndex > text.length
    ) continue;

    const key = `${citation.startIndex}:${citation.endIndex}:${url}`;
    if (seenRanges.has(key)) continue;
    seenRanges.add(key);
    normalized.push({
      type: "url_citation",
      startIndex: citation.startIndex,
      endIndex: citation.endIndex,
      url,
      title: sourceTitle(citation.title) ?? fallbackTitle(url),
    });
  }

  normalized.sort((left, right) => left.startIndex - right.startIndex || left.endIndex - right.endIndex || left.url.localeCompare(right.url));
  const sourceNumbers = new Map<string, number>();
  return normalized.map((citation) => {
    let sourceNumber = sourceNumbers.get(citation.url);
    if (sourceNumber === undefined) {
      sourceNumber = sourceNumbers.size + 1;
      sourceNumbers.set(citation.url, sourceNumber);
    }
    return { ...citation, sourceNumber };
  });
}

function clusterCitations(citations: readonly MarkdownCitation[]): CitationCluster[] {
  const clusters: CitationCluster[] = [];

  for (const citation of citations) {
    const previous = clusters.at(-1);
    if (!previous || citation.startIndex >= previous.endIndex) {
      clusters.push({ startIndex: citation.startIndex, endIndex: citation.endIndex, citations: [citation] });
      continue;
    }

    previous.endIndex = Math.max(previous.endIndex, citation.endIndex);
    previous.citations.push(citation);
  }

  return clusters;
}

function clusterMarkdown(cluster: CitationCluster): string {
  const seen = new Set<string>();
  const links: string[] = [];
  for (const citation of cluster.citations) {
    if (seen.has(citation.url)) continue;
    seen.add(citation.url);
    links.push(`[[${citation.sourceNumber}]](<${citation.url}>)`);
  }
  return links.join("");
}

/** Replaces annotated citation spans with stable, numbered, clickable Markdown links. */
export function formatWebSearchMarkdown(text: string, citations: readonly WebCitation[]): string {
  const clusters = clusterCitations(validMarkdownCitations(text, citations));
  let markdown = text;

  for (let index = clusters.length - 1; index >= 0; index -= 1) {
    const cluster = clusters[index];
    markdown = `${markdown.slice(0, cluster.startIndex)}${clusterMarkdown(cluster)}${markdown.slice(cluster.endIndex)}`;
  }

  return markdown;
}
